import { ThemedCard } from '@/app/platform/core/layout/themes/components/ThemedCard.tsx';
import { ThemedLoadingSpinner } from '@/app/platform/core/layout/themes/components/ThemedLoadingSpinner.tsx';
import { ThemedText } from '@/app/platform/core/layout/themes/components/ThemedText.tsx';
import { UserForm } from '@/app/platform/functions/people/users/UserForm.tsx';
import {
  AdminNavigation,
  adminMainThemeId,
} from '@/app/platform/core/layout/AdminNavigation.tsx';
import { AppLayout } from '@/app/platform/core/layout/AppLayout.tsx';
import { ThemeProvider } from '@/app/platform/core/layout/themes/ThemeContext.tsx';
import { useRepresentsByUserId } from '@/app/platform/functions/people/represents/useRepresents.ts';
import { useSyncRepresentedPersons } from '@/app/platform/functions/people/represents/useSyncRepresentedPersons.ts';
import {
  useUserMutations,
  useUserWithRolesAndPermissions,
} from '@/app/platform/functions/people/users/useUsers.ts';
import { User, UserCreate, UserUpdate } from '@/app/platform/functions/people/users/user.types.ts';

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const UserEditPageContent: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { user, loading: userLoading } = useUserWithRolesAndPermissions(
    userId ?? null,
  );
  const { represents, loading: representsLoading } = useRepresentsByUserId(
    userId ?? null,
  );
  const { updateUser } = useUserMutations();
  const { syncRepresentedPersons } = useSyncRepresentedPersons();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const breadcrumbs = useMemo(
    () => [
      { label: t('common.home'), path: '/app' },
      { label: t('common.admin'), path: '/admin' },
      { label: t('admin.people'), path: '/admin/people' },
      { label: t('admin.editUser') },
    ],
    [t],
  );

  const headerActions = <AdminNavigation currentPage="people" />;

  const handleSubmit = async (
    _data: UserCreate | UserUpdate,
    representPersonIds: string[],
  ) => {
    if (!user) return;
    setSubmitError(null);
    try {
      const data = _data as UserUpdate;
      await updateUser(user.id, data);
      await syncRepresentedPersons(user.id, represents, representPersonIds);

      navigate('/admin/people');
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  };

  if (userLoading || representsLoading)
    return (
      <AppLayout breadcrumbs={breadcrumbs} headerActions={headerActions}>
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}
        >
          <ThemedLoadingSpinner size="sm" />
        </div>
      </AppLayout>
    );

  if (!user)
    return (
      <AppLayout breadcrumbs={breadcrumbs} headerActions={headerActions}>
        <ThemedCard variant="danger">
          <ThemedText variant="danger">{t('admin.userNotFound')}</ThemedText>
        </ThemedCard>
      </AppLayout>
    );

  const initialRepresentPersonIds = represents.map((r) => r.person_id);

  return (
    <AppLayout breadcrumbs={breadcrumbs} headerActions={headerActions}>
      {submitError && (
        <div style={{ marginBottom: '16px' }}>
          <ThemedCard variant="danger">
            <ThemedText variant="danger">{submitError}</ThemedText>
          </ThemedCard>
        </div>
      )}
      <ThemedCard>
        <UserForm
          user={user as User}
          mode="edit"
          onSubmit={handleSubmit}
          onCancel={() => navigate('/admin/people')}
          initialRepresentPersonIds={initialRepresentPersonIds}
        />
      </ThemedCard>
    </AppLayout>
  );
};

export const AdminUserEditPage: React.FC = () => (
  <ThemeProvider defaultTheme={adminMainThemeId}>
    <UserEditPageContent />
  </ThemeProvider>
);
